import { FC, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  HStack,
  Icon,
  NewButton,
  Paragraph,
  useToast,
} from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import { CombinedStatusTypesEnum } from './types';

type InviteRowActionsProps = {
  inviteId: string;
  email: string;
  refetch: () => void;
  status: CombinedStatusTypesEnum;
};

export const InviteRowActions: FC<InviteRowActionsProps> = ({
  inviteId,
  email,
  refetch,
  status,
}) => {
  const { isDialogOpen, setIsDialogOpen } = useDialog(false);
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<'resend' | 'revoke'>('resend');
  const toast = useToast();

  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();

  const handleOnActionClick = (action: 'resend' | 'revoke') => {
    setAction(action);
    setIsDialogOpen(true);
  };

  const handleOnResendClick = async () => {
    setIsLoading(true);

    const response = await apiClient.tenantAdminUsers.resendInvite({
      params: { tenantId },
      body: { inviteId },
    });

    if (response.status !== 200) {
      toast.error('Invite email could not be resent.');
    }

    setIsDialogOpen(false);
    setIsLoading(false);
  };

  const handleOnRevokeClick = async () => {
    setIsLoading(true);

    const response = await apiClient.tenantAdminUsers.revokeInvite({
      params: { tenantId },
      body: { inviteId },
    });

    if (response.status === 200) {
      refetch();
    } else {
      toast.error('Invite could not be revoked.');
    }

    setIsDialogOpen(false);
    setIsLoading(false);
  };

  return (
    <HStack justify="end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={status !== 'PENDING'}>
          <NewButton type="outline" icon={{ name: 'Ellipsis' }} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          alignOffset={-4}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuItem onClick={() => handleOnActionClick('resend')}>
            <Icon name="RefreshCw" size="sm" />
            <span>Resend email</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOnActionClick('revoke')}>
            <Icon name="X" size="sm" />
            <span>Revoke invite</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={isDialogOpen}>
        <DialogContent close={false}>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
          </DialogHeader>
          <Paragraph color="secondary" size="md">
            {action === 'resend'
              ? `This will send another invitation email to "${email}".`
              : `This will block the user (${email}) from accepting the invite.`}
          </Paragraph>
          <DialogFooter>
            <NewButton
              disabled={isLoading}
              onClick={() => setIsDialogOpen(false)}
              text="Cancel"
              type="outline"
            />
            <NewButton
              loading={isLoading}
              onClick={
                action === 'resend' ? handleOnResendClick : handleOnRevokeClick
              }
              text={action === 'resend' ? 'Resend Email' : 'Revoke Invite'}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HStack>
  );
};
